import { useRef, useState } from 'react'
import css from 'classnames'
import { XYCoord, useDrag, useDrop } from 'react-dnd'
import { Link } from 'react-router-dom'
import { NestedCategories } from '~/shared/api'
import { DropZone } from './drop-zone'
import { SubItem3 } from './sub-item-3'
import { moveElement, replaceCategoriesById } from '../../lib/helpers'
import { CategoryStatusIndicators } from './category-status-indicators'
import { SVGDragIndicator, ArrowToDown, SVGPencil } from '~/shared/ui'

export interface Props {
  data: NestedCategories
  subList: NestedCategories[]
  list: NestedCategories[]
  index: number
  parentItem: NestedCategories
  setList: (data: NestedCategories[]) => void
  updateList: (draggingId: string, targetItem: NestedCategories) => void
  updated: () => void
}

export const SubItem2 = ({
  data,
  index,
  parentItem,
  list,
  subList,
  setList,
  updateList,
  updated,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)

  const [expanded, setExpanded] = useState(false)

  const [, drop] = useDrop({
    accept: 'sub-category-2',

    hover: (item: NestedCategories & { index: number }, monitor) => {
      if (!ref.current) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const hoveredRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoveredRect.bottom - hoveredRect.top) / 2
      const mousePosition = monitor.getClientOffset() as XYCoord
      const hoverClientY = mousePosition.y - hoveredRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      const newData = moveElement(subList, dragIndex, hoverIndex)
      const newList = replaceCategoriesById(list, parentItem.id, newData)

      setList(newList)
      updated()
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'sub-category-2',
    item: { ...data, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(ref)
  drop(preview(dragPreviewRef))

  return (
    <div
      ref={dragPreviewRef}
      className={css(
        'category active:overflow-hidden bg-white w-full overflow-hidden rounded-lg border border-[#E0E0E0]',
        {
          'h-[56px]': !expanded,
          'h-auto': expanded,
          'opacity-50': isDragging,
        },
      )}
      data-id={data.id}
    >
      <div className="flex h-[55px] min-w-0 items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center">
          <div
            ref={ref}
            className="flex cursor-grab item-center shrink-0 justify-center p-3 active:cursor-grabbing"
          >
            <SVGDragIndicator />
          </div>
          <h2
            className={css('min-w-0 flex-1 truncate text-[18px]', {
              'opacity-50': !data.showInMenu,
            })}
          >
            {data.name}
          </h2>
        </div>

        <div className="ml-3 flex shrink-0 items-center gap-3">
          <CategoryStatusIndicators
            enabled={data.enabled}
            showInMenu={data.showInMenu}
          />

          <Link
            to={`/categories/${data.id}/edit`}
            className="group flex shrink-0 items-center justify-center"
          >
            <div
              className={css(
                'cursor-pointer text-gray-400 group-hover:text-gray-600',
                {
                  'opacity-50': !data.showInMenu,
                },
              )}
            >
              <SVGPencil />
            </div>
          </Link>
        </div>
        <div
          onClick={() => setExpanded(!expanded)}
          className="ml-3 shrink-0 cursor-pointer"
        >
          <div className="px-4">
            <ArrowToDown
              className={css(
                'ml-auto mr-3 cursor-pointer transition-all duration-75',
                {
                  'rotate-180': expanded,
                },
              )}
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        </div>
      </div>

      <div className="px-3 pb-3">
        <hr />

        <div className="my-3 mb-4 flex gap-3">
          <div className="flex flex-1 flex-col gap-3">
            {data?.categories?.map((item, idx) => (
              <SubItem3
                key={item?.id}
                data={item}
                list={list}
                subList={data.categories || []}
                index={idx}
                setList={setList}
                parentItem={data}
                updated={updated}
              />
            ))}
          </div>
        </div>

        <div className="flex h-[100px] min-w-[350px] w-full flex-1">
          <DropZone updateList={updateList} parentElement={data} />
        </div>
      </div>
    </div>
  )
}
